import React from "react";
import MultiSelectDropdown from "@components/ui/customDropdowns/MultipInputSelection";
import ButtonLoader from "@components/ui/Loaders/ButtonLoader";
import NavigationButtons from "./NavigationButtons";
import { InputAdornment, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import MedicalViewMultipSelectDropdown from "@components/ui/customDropdowns/MedicalViewMultipSelectDropdown";
import ExercisePlanMultiSelectDropdown from "@components/ui/customDropdowns/ExercisePlanMultiSelectDropdown";

const GeneralStepForm = ({
  currentStep,
  steps,
  selectedTags,
  setSelectedTags,
  dynamicOptions,
  errors,
  setErrors,
  validateStep,
  setCurrentStep,
  loading,
  resetAdviceData,
  validateInput,
  isAdviceLoading,
  handleSubmit,
  handleSelecChange,
  handleInputChange,
  handleMultiSelectChange,
  removeTag,
}) => {
  const { t } = useTranslation();

  // Helper function to find display name by ID
  const findDisplayNameById = (key, id) => {
    const options = dynamicOptions[key];
    if (!options || !Array.isArray(options)) return id;

    if (key === "difficultiesLevel") {
      // Difficulties are simple strings, not objects with IDs
      return id ? id.charAt(0).toUpperCase() + id.slice(1) : id;
    }

    // For equipment and workouts, find by ID
    const option = options.find((opt) => opt.id === id);
    return option ? option.name : id;
  };

  // Get dropdown options for dynamic fields
  const getDynamicDropdownOptions = (key) => {
    const options = dynamicOptions[key] || [];

    if (key === "difficultiesLevel") {
      // Simple array of strings - store value directly
      return options.map((opt) => ({
        value: opt, // Store the value directly
        label: opt.charAt(0).toUpperCase() + opt.slice(1), // Display name
      }));
    } else {
      // Array of objects - store the ID
      return options.map((opt) => ({
        value: opt.id, // Store ID as value
        label: opt.name, // Display name
      }));
    }
  };

  // Create display map for multi-select dropdowns
  const getDisplayMap = (key) => {
    const options = dynamicOptions[key] || [];
    const displayMap = {};

    if (key === "difficultiesLevel") {
      options.forEach((opt) => {
        displayMap[opt] = opt.charAt(0).toUpperCase() + opt.slice(1);
      });
    } else {
      options.forEach((opt) => {
        displayMap[opt.id] = opt.name;
      });
    }

    return displayMap;
  };

  return (
    <div className="flex justify-center">
      <div className="grid sm:grid-cols-2 gap-0 sm:gap-10 w-full mt-2 md:mt-10">
        <div className="pt-0 md:pt-0 md:p-4 space-y-4">
          <h2 className="text-[20px] md:text-center font-[600] text-black-600">
            {steps[currentStep]?.title}
          </h2>
          <p className="text-[#0575E6] text-[16px] md:text-start !mt-2">
            {steps[currentStep]?.subtitle}
          </p>
          {steps[currentStep]?.options?.map(({ label, key, options, type }) => {
            // Check if this is one of the dynamic fields
            const isDynamicField = [
              "equipmentCategories",
              "difficultiesLevel",
              "workoutsCategories",
            ].includes(key);

            if (type !== "country-select") {
              return (
                <div key={key} className="flex flex-col gap-2 !m-0">
                  <>
                    <p className="text-black text-[14px] font-[Lato]">
                      {t(`firstFormSteps.${key}.label`)}
                    </p>

                    <div className="relative w-full mr-5">
                      {type === "multi-select" && (
                        <>
                          {isDynamicField ? (
                            // Dynamic multi-select fields (Workouts Categories)
                            (<ExercisePlanMultiSelectDropdown
                              options={getDynamicDropdownOptions(key).map(
                                (opt) => opt.value
                              )}
                              selectedValues={selectedTags[key] || []}
                              onSelect={(values) =>
                                handleMultiSelectChange(key, values)
                              }
                              label={label}
                              hasError={!!errors[key]}
                              placeholder="Select options"
                              isEditable={true}
                              isFistForm={true}
                              displayMap={getDisplayMap(key)}
                              fieldKey={key}
                            />)
                          ) : (
                            // Regular multi-select fields
                            (<MedicalViewMultipSelectDropdown
                              options={options.map((opt) => ({
                                value: opt,
                                label: t(
                                  `firstFormSteps.${key}.options.${opt}`
                                ),
                              }))}
                              selectedValues={selectedTags[key] || []}
                              onSelect={(selectedOptions) => {
                                const originalValues = selectedOptions.map(
                                  (opt) =>
                                    typeof opt === "object" ? opt.value : opt
                                );
                                handleMultiSelectChange(key, originalValues);
                              }}
                              isFistForm={true}
                              hasError={errors[key]}
                              label={t(`firstFormSteps.${key}.label`)}
                              placeholder={t(
                                "preferences.validation.onlyOne"
                              )}
                              isEditable={true}
                            />)
                          )}
                        </>
                      )}
                      {type === "select" ? (
                        <>
                          {isDynamicField ? (
                            // Dynamic select fields (Equipment Categories, Difficulties Level)
                            (<>
                              <select
                                className={`border p-2 rounded-md w-[100%] h-10 text-[12px] outline-none bg-white ${
                                  errors[key] ? "border-red-500" : ""
                                }`}
                                value={selectedTags[key] || ""}
                                onChange={(e) =>
                                  handleSelecChange(key, e.target.value)
                                }
                              >
                                <option value="" disabled>
                                  {t("preferences.validation.onlyOne")}
                                </option>
                                {getDynamicDropdownOptions(key).map(
                                  (option) => (
                                    <option
                                      key={option.value}
                                      value={option.value}
                                    >
                                      {option.label}
                                    </option>
                                  )
                                )}
                              </select>
                              {errors[key] && (
                                <div className="text-red-500 text-[10px] md:text-[12px] ml-2 mt-1">
                                  {/* {errors[key]} */}
                                  {t("validation.required", {
                                    field: t(`firstFormSteps.${[key]}.label`),
                                  })}
                                </div>
                              )}
                            </>)
                          ) : (
                            // Regular select fields
                            (<>
                              <select
                                className={`border p-2 rounded-md w-[100%] h-10 text-[12px] outline-none bg-white ${
                                  errors[key] ? "border-red-500" : ""
                                }`}
                                value={
                                  Array.isArray(selectedTags[key])
                                    ? selectedTags[key][
                                        selectedTags[key].length - 1
                                      ]
                                    : selectedTags[key] || ""
                                }
                                onChange={(e) =>
                                  handleSelecChange(key, e.target.value)
                                }
                              >
                                <option value="" disabled>
                                  {t("preferences.validation.onlyOne")}
                                </option>
                                {options.map((option) => (
                                  <option key={option} value={option}>
                                    {t(
                                      `firstFormSteps.${key}.options.${option}`
                                    )}
                                  </option>
                                ))}
                              </select>
                              {errors[key] && (
                                <div className="text-red-500 text-[10px] md:text-[12px] ml-2 mt-1">
                                  {errors[key]}
                                </div>
                              )}
                            </>)
                          )}
                        </>
                      ) : type === "numeric" ? (
                        <>
                          {label === "Weight" ? (
                            <div className="relative w-full">
                              <TextField
                                type="number"
                                variant="outlined"
                                fullWidth
                                size="small"
                                value={selectedTags.weight || ""}
                                onChange={(e) =>
                                  handleInputChange(
                                    "weight",
                                    e.target.value,
                                    "Weight"
                                  )
                                }
                                onKeyDown={(e) => {
                                  if (["e", "E", "+", "-"].includes(e.key)) {
                                    e.preventDefault();
                                  }
                                }}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    fontSize: "0.875rem",
                                    color: "black",
                                    height: "40px",
                                    "& fieldset": {
                                      borderColor: errors.weight
                                        ? "#EF4444"
                                        : "#e5e7eb",
                                      borderRadius: "0.375rem",
                                    },
                                    "&:hover fieldset": {
                                      borderColor: errors.weight
                                        ? "#EF4444"
                                        : "#e5e7eb",
                                    },
                                    "&.Mui-focused fieldset": {
                                      borderColor: errors.weight
                                        ? "#EF4444"
                                        : "#e5e7eb",
                                      boxShadow: "none",
                                      outline: "none",
                                      borderWidth: "1px",
                                    },
                                    "& input::placeholder": {
                                      fontSize: "0.9rem",
                                      opacity: 1,
                                      color: "#9CA3AF",
                                    },
                                  },
                                }}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <div className="flex items-center mr-1">
                                        {["kg", "Lbs"].map((unit) => (
                                          <button
                                            key={unit}
                                            onClick={() => {
                                              setSelectedTags((prev) => {
                                                const newUnit = unit;
                                                if (prev.weight) {
                                                  const convertedWeight =
                                                    newUnit === "Lbs"
                                                      ? prev.weight * 2.20462
                                                      : prev.weight / 2.20462;
                                                  return {
                                                    ...prev,
                                                    weightUnit: newUnit,
                                                    weight:
                                                      Math.round(
                                                        convertedWeight
                                                      ),
                                                  };
                                                }
                                                return {
                                                  ...prev,
                                                  weightUnit: newUnit,
                                                };
                                              });
                                            }}
                                            className={`px-2 py-0.5 border rounded-md mx-0.5 text-xs ${
                                              selectedTags.weightUnit === unit
                                                ? "bg-blue-500 text-white"
                                                : "bg-white text-black border-gray-300"
                                            }`}
                                            type="button"
                                            disabled={
                                              unit === selectedTags.weightUnit
                                            }
                                          >
                                            {t(
                                              `preferences.units.${unit.toLowerCase()}`
                                            )}
                                          </button>
                                        ))}
                                      </div>
                                    </InputAdornment>
                                  ),
                                }}
                                placeholder={`${t(
                                  `firstFormSteps.${key}.label`
                                )}`}
                              />
                            </div>
                          ) : (
                            <input
                              type="number"
                              className={`border p-2 rounded-md w-full h-10 text-sm ${
                                errors[key] ? "border-red-500" : ""
                              } focus:outline-none`}
                              value={selectedTags[key]}
                              min="1"
                              onChange={(e) =>
                                handleInputChange(key, e.target.value, label)
                              }
                              onKeyDown={(e) => {
                                if (["e", "E", "+", "-"].includes(e.key)) {
                                  e.preventDefault();
                                }
                              }}
                              placeholder={`${t(
                                `firstFormSteps.${key}.label`
                              )}`}
                            />
                          )}
                          {errors[key] && (
                            <div className="text-red-500 text-[10px] md:text-[12px] ml-2 mt-1">
                              {errors[key]}
                            </div>
                          )}
                        </>
                      ) : null}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {type !== "numeric" && (
                        <>
                          {selectedTags[key] &&
                            Array.isArray(selectedTags[key]) &&
                            selectedTags[key]?.map((tag, index) => (
                              <span
                                key={tag}
                                className="bg-blue-100 text-blue-600 px-2 py-1 rounded-md text-sm flex items-center"
                              >
                                {
                                  isDynamicField
                                    ? findDisplayNameById(key, tag) // Use dynamic display name
                                    : t(`firstFormSteps.${key}.options.${tag}`) // Use translation
                                }
                                <button
                                  onClick={() =>
                                    removeTag(key, selectedTags[key][index])
                                  }
                                  className="ml-1 text-blue-400 hover:text-blue-700"
                                >
                                  ✕
                                </button>
                              </span>
                            ))}
                        </>
                      )}
                    </div>
                  </>
                </div>
              );
            } else {
              return null;
            }
          })}
        </div>

        {steps[currentStep]?.src && (
          <div className="">
            <img
              src={steps[currentStep]?.src}
              alt="stepImage"
              className=" object-cover rounded-[15px] w-full min-h-[500px] max-h-[638px]  hidden sm:block "
            />
            <NavigationButtons
              currentStep={currentStep}
              steps={steps}
              loading={loading}
              isAdviceLoading={isAdviceLoading}
              validateStep={validateStep}
              setCurrentStep={setCurrentStep}
              handleSubmit={handleSubmit}
              selectedTags={selectedTags}
              medical={true}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default GeneralStepForm;
